import { NgRedux } from '@angular-redux/store';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Unsubscribe } from 'redux';
import 'rxjs/add/operator/takeUntil';
import { Subject } from 'rxjs/Subject';
import { APP_CONFIG } from '../../api/config';
import { ModifyReservationDelegate } from '../../api/delegates/modify-reservation.delegate';
import { Brands } from '../../api/enums/brands.enum';
import { IMessage } from '../../api/interfaces/Messages/IMessage';
import { ReservationErrorMessageKeys } from '../../api/model/errors/reservation-error-message-keys.model';
import { OverrideReservationGuestError } from '../../api/model/errors/reservation-error.model';
import { Hotel } from '../../api/model/hotel/hotel.model';
import { MessageCategory } from '../../api/model/message/message-category.model';
import { MessageType } from '../../api/model/message/message-type.model';
import { EarningsEstimate } from '../../api/model/reservation/points-estimate/earnings-estimate.model';
import { Reservation } from '../../api/model/reservation/reservation.model';
import { User } from '../../api/model/user';
import { MemberProgramType } from '../../api/model/user/member-program-type.model';
import { LogService, MemberService, TargetSystemService } from '../../api/services';
import { SiteURL, SiteURLUtils } from '../../common/site.url.utils';
import { ActionHandler } from '../../store/action.handler';
import { IAppConfig } from '../../store/interfaces/IAppConfig';
import { AppAction, AppActionState, IAppState } from '../../store/interfaces/IAppState';

const OVERRIDE_GUEST = true;

@Component({
  selector: 'reservation-association',
  templateUrl: './reservation-association.component.html',
  styleUrls: ['./reservation-association.component.scss']
})
export class ReservationAssociationComponent implements OnInit, OnDestroy {

  private readonly onDestroy = new Subject<void>();

  displayAssociation: boolean;
  associatedReservation: boolean;
  actionInProgress: boolean;
  overrideMode: boolean;
  joinMode: boolean;
  associationMode: boolean;
  recentJoin: boolean;
  recentAssociation: boolean;
  ambassadorFlow: boolean;
  emailRegistered: boolean | null;
  simplifiedLoginEnabled: boolean;
  welcomeMessage: string;
  pointsToEarn: number;
  milesToEarn: number;
  termsConditionsURL: string;
  customerCareURL: string;
  downloadURL: string;
  callBack: Function;

  state: IAppState;
  reservation: Reservation;
  member: User;
  hotel: Hotel;
  earningsEstimate: EarningsEstimate;

  unsubscribe: Unsubscribe;

  @Input() messages;

  @Output() onMessage: EventEmitter<any>;

  constructor(
    @Inject(APP_CONFIG) protected appConfig: IAppConfig,
    private _ngRedux: NgRedux<IAppState>,
    private _modifyReservationDelegate: ModifyReservationDelegate,
    private _logService: LogService,
    private _memberService: MemberService,
    private _targetSystemService: TargetSystemService) {
    this.onMessage = new EventEmitter<any>();
    this.callBack = this.associateAndScroll.bind(this);
  }

  ngOnInit() {
    this.emailRegistered = null;
    this.displayAssociation = false;
    this.associatedReservation = false;
    this.actionInProgress = false;
    this.overrideMode = false;
    this.joinMode = false;
    this.associationMode = false;
    this.recentJoin = false;
    this.recentAssociation = false;
    this.ambassadorFlow = false;
    this.simplifiedLoginEnabled = false;
    this._updateFromState();
    this._updatePointsEstimation();
    this.unsubscribe = this._ngRedux.subscribe(() => {
      this._updateFromState();
    });

    this._targetSystemService.getTargetSystem()
      .takeUntil(this.onDestroy)
      .subscribe(
        (targetSystemModel) => {
          this.simplifiedLoginEnabled = targetSystemModel.isGigyaSwitchOn;
        });
  }

  ngOnDestroy() {
    this.onDestroy.next();

    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  private _updateFromState() {
    this.state = this._ngRedux.getState();
    this.reservation = this.state.reservation;
    this.hotel = this.state.hotel;
    this.earningsEstimate = this.state.earningsEstimate;
    this.handleMember(this.state.member);
    this.initWelcomeState();
    this.checkEmailUsage();

    ActionHandler.handleSuccess(this.state, AppAction.GET_RESERVATION, () => {
      this._updatePointsEstimation();
    });
    ActionHandler.handleError(this.state, AppAction.ASSOCIATE_RESERVATION, () => {
      this.associationMode = false;
      if (this.state.error instanceof OverrideReservationGuestError) {
        this.overrideMode = true;
        return;
      }
      this.onMessage.emit({category: MessageCategory.ASSOCIATE_RESERVATION, message: this._getMessage(ReservationErrorMessageKeys.ASSOCIATION_ERROR_UNKNOWN_KEY)});
    });

    ActionHandler.handleError(this.state, AppAction.JOIN_ASSOCIATE_RESERVATION, () => {
      this.joinMode = false;
      this.onMessage.emit({category: MessageCategory.ASSOCIATE_RESERVATION, message: this._getMessage(ReservationErrorMessageKeys.JOIN_ERROR_UNKNOWN_KEY)});
    });
  }

  private handleMember(updated: User) {
    if ((!this.member && !!updated) || (!!updated && !this.isSameLoginState(this.member, updated))) {
      this.member = updated;
      this._updatePointsEstimation();
    }
    this.member = updated;
  }

  private isSameLoginState(one: User, two: User): boolean {
    return one.loginStatus === two.loginStatus;
  }

  private initWelcomeState() {
    this.displayAssociation = false;
    if (!this.reservation || !this.member || !this.earningsEstimate || !this.messages) {
      return;
    }
    if (this.reservation.guest.rewardsClubNumber &&
      this.reservation.guest.rewardsClubNumber !== this.member.rewardsClubNumber) {
      return;
    }
    this.associatedReservation = !!this.reservation.guest.rewardsClubNumber;
    if (this.associatedReservation && this.member.isAnonymous()) {
      return;
    }
    this.displayAssociation = true;
    this.recentJoin = this.recentJoin || (this.joinMode && this.associatedReservation);
    this.recentAssociation = this.recentAssociation || (this.associationMode && this.associatedReservation);
    this.actionInProgress = this._isJoinInProgress() || this._isAssociationInProgress();
    this.ambassadorFlow = ((this.member.isAmbassador() && !this.member.isAnonymous()) || this.member.isAnonymous()) && this.hotel.chainCode.toLowerCase() === Brands.INTERCONTINENTAL;
    this.termsConditionsURL = SiteURLUtils.getLocalizedTermsConditionsUrl(this.appConfig);
    this.downloadURL = SiteURLUtils.getLocalizedAppGetUrl(this.appConfig);
    this.customerCareURL = SiteURLUtils.getLocalizedCustomerCareUrl(this.appConfig);
    const programMessageKey = this.ambassadorFlow ? MemberProgramType.AMBASSADOR : MemberProgramType.REWARDS_CLUB;
    const earningMessageKey = this.earningsEstimate.points && this.earningsEstimate.miles ? 'points-miles' : this.earningsEstimate.miles ? 'miles' : 'points';
    const associationMessageKey = (this.recentJoin && this.member.isExplicit()) ? 'welcome' : (this.recentAssociation && this.member.isExplicit()) ? 'success' : this.associatedReservation ? 'associated' : 'intro';
    const welcomeMessageKey = ['association', programMessageKey, this.member.loginStatus, earningMessageKey, associationMessageKey].join('.').toLowerCase();

    this.pointsToEarn = this.earningsEstimate.points ? this.earningsEstimate.points.totalEarnings.amount : 0;
    this.milesToEarn = this.earningsEstimate.miles ? this.earningsEstimate.miles.totalEarnings.amount : 0;
    this.welcomeMessage = this.messages[welcomeMessageKey] || welcomeMessageKey;
  }

  private checkEmailUsage() {
    if (!!this.reservation && this.emailRegistered == null) {
      this.emailRegistered = false;

      const email = this.reservation.communicationPreferences.emailAddress;
      const firstName = this.reservation.guest.firstName;
      const lastName = this.reservation.guest.lastName;
      const hotelMnemonic = this.reservation.hotelMnemonic;
      const checkInDate = this.reservation.stay.checkInDate;
      const checkOutDate = this.reservation.stay.checkOutDate;

      this._memberService.isRegisteredEmail(email, firstName, lastName, hotelMnemonic, checkInDate, checkOutDate)
        .subscribe(status => {
            this.emailRegistered = status;
          },
          error => {
            this._logService.warn(`Error getting email usage status: ${email}`, 'CLASS_NAME', error);
          }
        );
    }
  }

  isJoinAllowed() {
    return this.member.isAnonymous() && this.emailRegistered === false;
  }

  associate() {
    this.actionInProgress = true;
    this.associationMode = true;
    this.onMessage.emit({category: MessageCategory.ASSOCIATE_RESERVATION, message: null});
    this._modifyReservationDelegate.associateReservation(this.reservation, this.member, this.simplifiedLoginEnabled);
  }

  associateAndScroll() {
    if (!this.member.isAnonymous()) {
      this.associate();
    }
  }

  override() {
    this.actionInProgress = true;
    this.associationMode = true;
    this.overrideMode = false;
    this.onMessage.emit({category: MessageCategory.ASSOCIATE_RESERVATION, message: null});
    this._modifyReservationDelegate.associateReservation(this.reservation, this.member, OVERRIDE_GUEST);
  }

  cancelOverride() {
    this.overrideMode = false;
    this.associationMode = false;
  }

  join() {
    if (this.ambassadorFlow) {
      SiteURLUtils.redirectToAmbassadorJoin(this.appConfig);
    } else {
      this.actionInProgress = true;
      this.joinMode = true;
      this.onMessage.emit({category: MessageCategory.ASSOCIATE_RESERVATION, message: null});
      this._modifyReservationDelegate.joinAndAssociateReservation(this.reservation, this.simplifiedLoginEnabled);
    }
  }

  private _updatePointsEstimation() {
    if (!!this.reservation) {
      this._modifyReservationDelegate.fetchPointsEstimate(this.reservation);
    }
  }

  private _isError(action: AppAction) {
    return (this.state.actionType === action && this.state.state === AppActionState.ERROR);
  }

  private _isJoinInProgress() {
    return this.joinMode && !this.recentJoin && !this._isError(AppAction.JOIN_ASSOCIATE_RESERVATION);
  }

  private _isAssociationInProgress() {
    return this.associationMode && !this.recentAssociation && !this._isError(AppAction.ASSOCIATE_RESERVATION);
  }

  private _getMessage(messageKey) {
    const customerCareParam = [SiteURLUtils.getLocalizedSiteUrl(this.appConfig, SiteURL.CUSTOMER_CARE)];
    return <IMessage>{key: messageKey, messageType: MessageType.WARNING, params: customerCareParam};
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/components/reservation-association/reservation-association.component.ts